import { DriveCommand } from 'core/models/drive';
import { Environment } from 'core/models/environment';
import { Project } from 'core/models/project';
import { Vehicle } from 'core/models/vehicle';
import { RTCTask } from 'core/models/rtc';
import {
    RTCRequestListener,
    RTCResponseListener,
    VehicleRepository,
    VehicleStatusErrorListener,
    VehicleStatusListener,
} from './VehicleRepository';
import { Lazy } from '../../utility/Lazy';
import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';

export class CachedVehicleRepository implements VehicleRepository {
    private vehicleCache = new Map<string, Lazy<{ vehicle: Vehicle, updateAt: number }>>();
    private vehiclesCache = new Map<string, Lazy<{ vehicles: Vehicle[], updateAt: number }>>();

    constructor(private readonly underlying: VehicleRepository, private readonly timeout: number = 3 * 60 * 1000 /* [ms] */) {
        //
    }

    async getVehicles(
        projectId: Project.Id,
        environmentId: Environment.Id,
        options?: { signal?: AbortSignal },
    ): Promise<Vehicle[]> {
        const environmentKey = `${projectId}-${environmentId}`;
        const cached = this.vehiclesCache.get(environmentKey);
        if (cached) {
            try {
                const entry = await cached.get();
                if (Date.now() - entry.updateAt < this.timeout) {
                    return entry.vehicles;
                }
            } catch {
                // Ignore errors and fetch fresh data
            }
        }

        const lazy = new Lazy((async () => {
            const vehicles = options !== undefined
                ? await this.underlying.getVehicles(projectId, environmentId, options)
                : await this.underlying.getVehicles(projectId, environmentId);
            const updateAt = Date.now();
            vehicles.forEach(vehicle => {
                const vehicleKey = `${projectId}-${environmentId}-${vehicle.id}`;
                this.vehicleCache.set(vehicleKey, new Lazy(Promise.resolve({ vehicle, updateAt })));
            });
            return { vehicles, updateAt };
        })());
        this.vehiclesCache.set(environmentKey, lazy);

        const entry = await lazy.get();
        return entry.vehicles;
    }

    async getVehicle(
        id: Vehicle.Id,
        projectId: Project.Id,
        environmentId: Environment.Id,
    ): Promise<Vehicle> {
        const vehicleKey = `${projectId}-${environmentId}-${id}`;
        const cached = this.vehicleCache.get(vehicleKey);
        if (cached) {
            try {
                const entry = await cached.get();
                if (Date.now() - entry.updateAt < this.timeout) {
                    return entry.vehicle;
                }
            } catch {
                // Ignore errors and fetch fresh data
            }
        }

        const lazy = new Lazy((async () => {
            const vehicle = await this.underlying.getVehicle(id, projectId, environmentId);
            const updateAt = Date.now();
            this.vehicleCache.set(vehicleKey, new Lazy(Promise.resolve({ vehicle, updateAt })));
            return { vehicle, updateAt };
        })());
        this.vehicleCache.set(vehicleKey, lazy);

        const entry = await lazy.get();
        return entry.vehicle;
    }


    addVehicleStatusListener(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        listener: VehicleStatusListener,
    ): void {
        return this.underlying.addVehicleStatusListener(vehicleId, projectId, listener);
    }

    removeVehicleStatusListener(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        listener: VehicleStatusListener,
    ): void {
        return this.underlying.removeVehicleStatusListener(vehicleId, projectId, listener);
    }

    addVehicleStatusErrorListener(listener: VehicleStatusErrorListener): void {
        return this.underlying.addVehicleStatusErrorListener(listener);
    }

    removeVehicleStatusErrorListener(listener: VehicleStatusErrorListener): void {
        return this.underlying.removeVehicleStatusErrorListener(listener);
    }

    postDriveCommand(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        environmentId: Environment.Id,
        command: Partial<DriveCommand>,
    ): Promise<void> {
        return this.underlying.postDriveCommand(vehicleId, projectId, environmentId, command);
    }

    cancelPostDriveCommand(vehicle: Vehicle.Id): Promise<void> {
        return this.underlying.cancelPostDriveCommand(vehicle);
    }

    postHeartbeat(vehicleId: Vehicle.Id, projectId: Project.Id): void {
        return this.underlying.postHeartbeat(vehicleId, projectId);
    }

    addVehicleHeartbeatListener(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        listener: (timestamp: number, accept: boolean) => void,
    ): void {
        return this.underlying.addVehicleHeartbeatListener(vehicleId, projectId, listener);
    }

    removeVehicleHeartbeatListener(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        listener: (timestamp: number, accept: boolean) => void,
    ): void {
        return this.underlying.removeVehicleHeartbeatListener(vehicleId, projectId, listener);
    }

    addRTCRequestListener(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        listener: RTCRequestListener,
    ): void {
        return this.underlying.addRTCRequestListener(vehicleId, projectId, listener);
    }

    removeRTCRequestListener(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        listener: RTCRequestListener,
    ): void {
        return this.underlying.removeRTCRequestListener(vehicleId, projectId, listener);
    }

    addRTCCommandResponseListener(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        listener: RTCResponseListener,
    ): void {
        return this.underlying.addRTCCommandResponseListener(vehicleId, projectId, listener);
    }

    removeRTCCommandResponseListener(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        listener: RTCResponseListener,
    ): void {
        return this.underlying.removeRTCCommandResponseListener(vehicleId, projectId, listener);
    }

    postRTCCommand(
        vehicleId: Vehicle.Id,
        projectId: Project.Id,
        environmentId: Environment.Id,
        task: RTCTask,
        activated: boolean,
        transactionId: string,
    ): Promise<void> {
        return this.underlying.postRTCCommand(vehicleId, projectId, environmentId, task, activated, transactionId);
    }

    getWebSocket?(): StableWebSocket | undefined {
        return this.underlying.getWebSocket?.();
    }
}
